import React from "react";
import { REGION_GROUPS } from "../data/regionGroups";

/**
 * Chip row that lets a therapist add/remove a whole anatomical region to
 * the selection in one tap.
 *
 * A region is considered "active" when *every* muscle in its group is
 * already in the current selection. Tapping an active region removes its
 * muscles; tapping an inactive region adds them (union — no duplicates).
 */
export default function RegionalQuickSelect({ selected = [], onChange }) {
  const selectedSet = React.useMemo(() => new Set(selected), [selected]);

  const toggleRegion = (region) => {
    const allIn = region.muscles.every((m) => selectedSet.has(m));
    let next;
    if (allIn) {
      next = selected.filter((m) => !region.muscles.includes(m));
    } else {
      const merged = new Set(selected);
      region.muscles.forEach((m) => merged.add(m));
      next = Array.from(merged);
    }
    onChange?.(next);
  };

  return (
    <div data-testid="regional-quick-select">
      <div className="label-overline mb-2">Quick select region</div>
      <div className="flex flex-wrap gap-2">
        {REGION_GROUPS.map((region) => {
          const allIn = region.muscles.every((m) => selectedSet.has(m));
          const someIn = !allIn && region.muscles.some((m) => selectedSet.has(m));
          const cls = allIn
            ? "bg-sage-600 text-white border-sage-600 hover:bg-sage-700"
            : someIn
            ? "bg-terracotta-500/15 text-terracotta-600 border-terracotta-500/50 hover:bg-terracotta-500/25"
            : "bg-white text-ink-700 border-paper-200 hover:bg-paper-100";
          return (
            <button
              key={region.id}
              type="button"
              onClick={() => toggleRegion(region)}
              data-testid={`region-chip-${region.id}`}
              className={`min-h-[36px] px-3 rounded-full text-xs font-medium border transition-all ${cls}`}
              aria-pressed={allIn}
            >
              {region.label}
              {someIn && <span className="ml-1 opacity-70">·</span>}
            </button>
          );
        })}
      </div>
    </div>
  );
}
